import { APIError } from "@certn/api";
import {
  ButtonGroup,
  GhostButton,
  PrimaryButton,
  SectionMessage,
  Space,
  showToast,
  useColors,
} from "@certn/cascadia-react";
import { getTextStyle } from "@certn/cascadia-react/foundations/typography/styles";
import { CascadiaJsonFormContext, CascadiaLayoutProps } from "@certn/forms";
import { RenderError } from "@certn/forms/src/components/helpers/RenderError";
import { Text } from "@certn/forms/src/components/Text";
import { BaseLayout } from "@certn/forms/src/layouts/BaseLayout";
import { BaseLayoutContent } from "@certn/forms/src/layouts/BaseLayoutContent";
import { hasLabel } from "@certn/forms/src/utils";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { useJsonForms } from "@jsonforms/react";
import * as Sentry from "@sentry/react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { useApplication } from "../ApplicationContext";
import {
  ConsentRequiredModal,
  buildSignedConsentDialogContent,
} from "../controls/consent-control/ConsentRequiredModal";
import { useConsentStepValidation } from "../utils";

interface QuickscreenConsentBaseLayoutProps {
  layoutProps: CascadiaLayoutProps;
  testID: string;
}

/**
 * Shared chrome for Quickscreen consent step layouts:
 * title/subtitle, optional section message, stacked content with separators,
 * and a Back/Next action bar that partially saves progress.
 */
export function QuickscreenConsentBaseLayout({
  layoutProps,
  testID,
}: QuickscreenConsentBaseLayoutProps) {
  const { t } = useTranslation();
  const colors = useColors();
  const { isSubmitting, submit } = useContext(CascadiaJsonFormContext);
  const { prevSection, navigateToSection, partiallySaveData } =
    useApplication();
  const jsonFormsCtx = useJsonForms();

  const { uischema } = layoutProps;
  const options = uischema.options || {};

  const { showValidationModal, setShowValidationModal, handleNextStep } =
    useConsentStepValidation(
      layoutProps.schema,
      uischema,
      submit,
      jsonFormsCtx.core?.data ?? {}
    );

  if (!hasLabel(uischema)) {
    return <RenderError>{t("Invalid Category")}</RenderError>;
  }

  return (
    <>
      <BaseLayout testID={testID} {...layoutProps} className={styles.layout}>
        <div className={styles.heading}>
          <h1
            className={styles.label}
            data-testid={`${slugify(layoutProps.path)}-title`}
          >
            {uischema.label}
          </h1>
          {options.subLabel && (
            <Text
              style="form/body-primary"
              color="text-weak"
              data-testid={`${slugify(layoutProps.path)}-sub-title`}
            >
              {options.subLabel}
            </Text>
          )}
          {options.sectionMessage && (
            <SectionMessage
              type={options.sectionMessage.type}
              sectionTitle={options.sectionMessage.heading}
              icon={options.sectionMessage.type}
              testID="category-section-message"
            >
              {options.sectionMessage.content}
            </SectionMessage>
          )}
        </div>

        <div className={styles.content}>
          <BaseLayoutContent />
        </div>
      </BaseLayout>

      <div
        className={css(styles.actionBar, {
          borderTop: `1px solid ${colors["border-base"]}`,
        })}
      >
        <ButtonGroup justify="end">
          {prevSection && (
            <GhostButton
              leftIcon="chevron_left"
              onPress={async () => {
                try {
                  await partiallySaveData();
                } catch (e) {
                  if (e instanceof APIError && e.status === 400) {
                    showToast(t("Error saving your progress"), {
                      type: "error",
                    });
                  } else {
                    showToast(t("Something went wrong, try again"), {
                      type: "error",
                    });
                    Sentry.captureException(e);
                  }
                }

                navigateToSection(prevSection);
              }}
              testID="section-back-button"
            >
              {t("Back")}
            </GhostButton>
          )}
          <Space />
          <PrimaryButton
            onPress={handleNextStep}
            variant="brand-primary"
            isLoading={isSubmitting}
            testID="section-next-step-button"
          >
            {t("Next step")}
          </PrimaryButton>
        </ButtonGroup>
      </div>

      <ConsentRequiredModal
        show={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        alternativeDialogContent={buildSignedConsentDialogContent(
          (uischema.label as string) || t("Consent Form"),
          t,
          { isQuickscreen: true }
        )}
      />
    </>
  );
}

const styles = {
  layout: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  label: css(getTextStyle("form/page-title"), { margin: 0 }),
  content: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
    '> :not([aria-hidden="true"]):not(:last-child)': {
      paddingBottom: "var(--space-4)",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "var(--border-base)",
    },
  }),
  actionBar: css({
    paddingTop: `calc(${"var(--space-4)"} - 1px)`,
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
  }),
};
